/**
 * Persistência do blob criptografado no IndexedDB.
 *
 * Estrutura:
 *   db "goyaz-keystore" v1
 *   store "keys" (key = "owner")
 *     { ciphertext, salt, iv, address, createdAt }
 *
 * O `address` (endereço público derivado da private key) é guardado
 * em claro pra UI poder mostrar antes de desbloquear.
 */
import type { EncryptedBlob } from "./crypto";

const DB_NAME = "goyaz-keystore";
const DB_VERSION = 1;
const STORE = "keys";
const KEY = "owner";

export interface StoredKey extends EncryptedBlob {
  address: string;
  createdAt: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveKey(key: StoredKey): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(key, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function loadKey(): Promise<StoredKey | null> {
  const db = await openDB();
  const result = await new Promise<StoredKey | null>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve((req.result as StoredKey) ?? null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return result;
}

export async function deleteKey(): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function hasKey(): Promise<boolean> {
  const k = await loadKey();
  return k !== null;
}
